/**
 * Uplifted from `utils/OfflineUtil` (apps/web 164 lines, apps/mobile 334) — SHARED half.
 *
 * The IndexedDB/native offline data layer. `del`/`replace`/`get`/`put` diverge completely and
 * live in the two variants; the two CALLBACK RESOLVERS below are byte-identical in both apps
 * (modulo quote style) and live here.
 *
 * 🔴 **This file deliberately imports `../nativeHooks/shared` and NOT the `../nativeHooks`
 * FOLDER, and that is what keeps `packages/core` acyclic.** The app's own graph is circular —
 * `OfflineUtil` -> `nativeHooks.execMobileServer` -> `utils/localServer.fnServerExec` ->
 * `OfflineUtil.handleLocalServerCallBack`. It survives there only because the back-edge is
 * called at RUNTIME, never at module evaluation. Reaching `callbackObj` through the folder would
 * rebuild that loop inside core (`OfflineUtil/shared` -> `nativeHooks/index.native` ->
 * `localServer/index.native` -> `OfflineUtil/shared`); importing the leaf `shared.ts` directly
 * breaks it, because `nativeHooks/shared.ts` imports nothing.
 *
 * ⚠️ `callbackObj` MUST be the same object the two `exec`s write to — see `nativeHooks/shared.ts`.
 * ⚠️ `pos_errormap` IS reached through the FOLDER, and must be: it is genuinely divergent
 * (web 927 codes, mobile 924), so a `shared.ts` has to let the bundler pick per platform —
 * ledger hazard 16's required form.
 */
import { callbackObj } from "../nativeHooks/shared";
import { pos_errormap } from "../posErrors";
import { notifyError } from "../../effects-bridge";

/**
 * Resolves a pending native DB `get`. Byte-identical in both apps.
 *
 * ⚠️ Note the asymmetry with `handleLocalServerCallBack` below: this one reports errors to the
 * CONSOLE and still invokes the parked callback with `(success, error)`, while the LAN-server one
 * shows a toast and does NOT invoke the callback at all. Preserved verbatim on both counts.
 */
export function handleGetCallback(response: any) {
  console.log("handleGetCallback", response);
  if (response?.error) {
    console.error("DB get", response?.error);
    return;
  }
  if (callbackObj[response.id]) {
    callbackObj[response.id]?.callback(response.success, response?.error);
  }
}

/**
 * Resolves a reply from the LAN master. Byte-identical in both apps.
 *
 * ⚠️ `ToastNotifyError` becomes `notifyError` — the effects-bridge seam. Both apps import their
 * own toast here (mobile from `@app/commonComponents/ToastNotify`), and the bridge is how core
 * reaches either one.
 *
 * ⚠️ Requires BOTH a parked callback and `response.data` before invoking, and unwraps
 * `response.data.data` — one level deeper than `handleGetCallback`. Verbatim.
 */
export function handleLocalServerCallBack(response: any) {
  console.log("handleLocalServerCallback", response);
  if (response.error) {
    notifyError(pos_errormap["ERR_SERVER_CLIENT_CONNECTION"]);
    return;
  }
  if (callbackObj[response.id] && response.data) {
    callbackObj[response.id].callback(response.data.data);
  }
}
